<template>
  <header>
    <!-- Niveau 1 : Logo, recherche, actions -->
    <div class="header-top">
      <div class="container">
        <div class="header-top-inner">
          <!-- Logo -->
          <router-link to="/" class="logo">
            <img src="/logo192.png" alt="OKOLISY" class="logo-img">
          </router-link>
          
          <!-- Barre de recherche -->
          <div class="search-wrapper">
            <div class="search-container">
              <div class="search-box">
                <div class="search-type-selector">
                  <button 
                    class="type-btn"
                    :class="{ active: searchType === 'articles' }"
                    @click="searchType = 'articles'"
                  >
                    Articles
                  </button>
                  <button 
                    class="type-btn"
                    :class="{ active: searchType === 'users' }"
                    @click="searchType = 'users'"
                  >
                    Membres
                  </button>
                </div>

                <div class="search-input-wrapper">
                  <font-awesome-icon icon="search" class="search-icon" />
                  <input 
                    type="text" 
                    v-model="searchQuery"
                    :placeholder="searchType === 'articles' ? 'Rechercher un article...' : 'Rechercher un membre...'"
                    class="search-input"
                    @keyup.enter="executeSearch"
                  />
                  <button v-if="searchQuery" class="clear-search" @click="clearSearch">
                    <font-awesome-icon icon="times" />
                  </button>
                </div>

                <button class="search-btn" @click="executeSearch">
                  <font-awesome-icon icon="search" />
                </button>
              </div>
            </div>
          </div>
          
          <div class="nav-actions">
            <!-- ✅ Bouton Vendre - texte complet -->
            <a 
              href="#" 
              class="btn-sell" 
              @click.prevent="handleSellClick"
            >
              <font-awesome-icon icon="plus" />
              <span>Vends tes articles</span>
            </a>

            <!-- ✅ Cloche de notifications -->
            <NotificationBell v-if="isAuthenticated" />
            
            <!-- Menu utilisateur -->
            <div class="user-menu" @click="toggleUserMenu" ref="userMenuRef">
              <!-- Avatar -->
              <div class="user-avatar">
                <img 
                  :src="userPhoto" 
                  :alt="userPseudo || 'User'"
                  class="avatar-img"
                  @error="handleAvatarError"
                >
              </div>
              
              <!-- ✅ Pseudo - toujours visible -->
              <span class="user-pseudo">{{ userPseudo || 'Compte' }}</span>
              
              <!-- Chevron -->
              <span class="chevron-icon" :class="{ rotated: showUserMenu }">
                <font-awesome-icon icon="chevron-down" />
              </span>
              
              <transition name="fade">
                <div v-if="showUserMenu" class="dropdown-menu">
                  <template v-if="!isAuthenticated">
                    <a href="#" class="dropdown-item" @click.prevent="showLoginModal">
                      <font-awesome-icon icon="sign-in-alt" />
                      Se connecter
                    </a>
                    <a href="#" class="dropdown-item" @click.prevent="showRegisterModal">
                      <font-awesome-icon icon="user-plus" />
                      S'inscrire
                    </a>
                  </template>
                  <template v-else>
                    <!-- ✅ Pseudo en premier dans le dropdown -->
                    <div class="dropdown-item pseudo-display">
                      <font-awesome-icon icon="user" />
                      <span class="pseudo-full">{{ userPseudo }}</span>
                    </div>
                    <hr class="dropdown-divider">
                    <router-link to="/mon-profil" class="dropdown-item" @click="closeUserMenu">
                      <font-awesome-icon icon="cog" />
                      Mon profil
                    </router-link>
                    <router-link to="/mes-annonces" class="dropdown-item" @click="closeUserMenu">
                      <font-awesome-icon icon="box" />
                      Mes annonces
                    </router-link>
                    <router-link to="/mes-commandes" class="dropdown-item" @click="closeUserMenu">
                      <font-awesome-icon icon="shopping-bag" />
                      Mes commandes
                    </router-link>
                    <router-link to="/mes-favoris" class="dropdown-item" @click="closeUserMenu">
                      <font-awesome-icon icon="heart" />
                      Mes favoris
                    </router-link>
                    <hr class="dropdown-divider">
                    <a href="#" class="dropdown-item logout" @click.prevent="handleLogout">
                      <font-awesome-icon icon="sign-out-alt" />
                      Déconnexion
                    </a>
                  </template>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Niveau 2 : Catégories -->
    <div class="header-bottom">
      <div class="container">
        <div class="categories-scroll">
          <router-link 
            to="/" 
            class="category-link"
            :class="{ active: !selectedCategoryId && currentRoute === '/' }"
            @click="selectMainCategory(null)"
          >
            Tous les articles
          </router-link>
          <router-link 
            v-for="cat in mainCategories" 
            :key="cat.id"
            to="/"
            class="category-link"
            :class="{ active: selectedCategoryId === cat.id }"
            @click="selectMainCategory(cat.id)"
          >
            {{ cat.nom }}
          </router-link>
        </div>
      </div>
    </div>

    <AuthModals 
      :show-login-modal="showLogin" 
      :show-register-modal="showRegister"
      @close-modals="closeModals"
      @switch-to-login="switchToLogin"
      @switch-to-register="switchToRegister"
      @login-success="handleAuthSuccess"
      @register-success="handleAuthSuccess"
    />
  </header>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useClientAuthStore } from '@/Auth/stores/clientAuthStore';
import AuthModals from '@/Auth/AuthModals.vue';
import { $api } from '@/main';
import NotificationBell from '@/components/Notifications/NotificationBell.vue';

interface User {
  id: number;
  pseudo: string;
  email: string;
  numero_telephone: string;
  adresse: string;
  ville: string;
  role: string;
  photo_profil?: string | null;
}

export default defineComponent({
  name: 'AppHeader',
  components: {
    AuthModals,
    NotificationBell
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const clientAuth = useClientAuthStore();
    
    const searchQuery = ref('');
    const searchType = ref<'articles' | 'users'>('articles');
    const showLogin = ref(false);
    const showRegister = ref(false);
    const showUserMenu = ref(false);
    const userMenuRef = ref<HTMLElement | null>(null);
    const mainCategories = ref<any[]>([]);
    const selectedCategoryId = ref<number | null>(null);
    const currentRoute = ref(route.path);
    const userPhoto = ref('');

    const isAuthenticated = computed(() => clientAuth.state.isAuthenticated);
    
    const userPseudo = computed(() => {
      return clientAuth.state.user?.pseudo || 'Compte';
    });

    // ✅ MÉTHODE UNIFIÉE POUR CHARGER LA PHOTO
    const loadUserPhoto = () => {
      const user = clientAuth.state.user as User | null;
      if (!user) {
        userPhoto.value = '/icone_user_defaut_orange.png';
        return;
      }
      
      const photo = user.photo_profil;
      
      if (!photo || photo === 'null' || photo === '' || photo === 'undefined') {
        userPhoto.value = '/icone_user_defaut_orange.png';
        return;
      }
      
      if (photo.startsWith('http')) {
        userPhoto.value = photo;
        return;
      }
      
      // ✅ EN PRODUCTION : utiliser l'URL complète de l'API
      // ✅ EN DÉVELOPPEMENT : utiliser le chemin relatif
      if (process.env.NODE_ENV === 'production') {
        // En production, l'API est sur api.okolisy.com
        userPhoto.value = `https://api.okolisy.com/storage/profiles/${photo}`;
      } else {
        userPhoto.value = `/storage/profiles/${photo}`;
      }
    };

    const handleAvatarError = () => {
      userPhoto.value = '/icone_user_defaut_orange.png';
    };

    // ✅ Écouter l'événement de mise à jour de la photo
    const handlePhotoUpdated = (event: Event) => {
      const customEvent = event as CustomEvent;
      const photoUrl = customEvent.detail?.photoUrl;
      
      if (photoUrl) {
        // ✅ Utiliser l'URL complète reçue de l'événement
        userPhoto.value = photoUrl;
        
        // ✅ Mettre à jour le store avec le nom du fichier
        const currentUser = clientAuth.state.user;
        if (currentUser) {
          const fileName = photoUrl.split('/').pop();
          if (fileName && !fileName.startsWith('icone_user_defaut_orange')) {
            (currentUser as any).photo_profil = fileName;
            clientAuth.state.user = { ...currentUser };
            
            // Mettre à jour localStorage
            const userStr = localStorage.getItem('client_user');
            if (userStr) {
              const userData = JSON.parse(userStr);
              userData.photo_profil = fileName;
              localStorage.setItem('client_user', JSON.stringify(userData));
            }
          } else {
            // Si c'est l'image par défaut
            (currentUser as any).photo_profil = null;
            clientAuth.state.user = { ...currentUser };
          }
        }
      } else {
        // Recharger la photo depuis le store
        loadUserPhoto();
      }
    };

    watch(() => route.path, (newPath) => {
      currentRoute.value = newPath;
    });

    watch(() => clientAuth.state.user, () => {
      loadUserPhoto();
    }, { deep: true });

    const loadMainCategories = async () => {
      try {
        const response = await $api.get('/api/categories');
        if (response.data.success) {
          mainCategories.value = response.data.categories;
        }
      } catch (error) {
        console.error('Erreur chargement catégories:', error);
      }
    };

    const handleSellClick = (event: Event) => {
      event.preventDefault();
      if (!isAuthenticated.value) {
        showLoginModal();
      } else {
        router.push('/vendre');
      }
    };

    const selectMainCategory = (categoryId: number | null) => {
      selectedCategoryId.value = categoryId;
      
      if (router.currentRoute.value.path === '/') {
        window.dispatchEvent(new CustomEvent('category-filter', { 
          detail: { categoryId: categoryId || null } 
        }));
      } else {
        router.push({ path: '/', query: { category: categoryId?.toString() || '' } });
      }
    };

    const executeSearch = () => {
      if (!searchQuery.value.trim()) {
        router.push({ path: '/', query: {} });
        return;
      }
      
      if (searchType.value === 'articles') {
        router.push({ path: '/', query: { search: searchQuery.value } });
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('search-products', { 
            detail: { query: searchQuery.value } 
          }));
        }, 50);
      } else {
        router.push({ path: '/recherche/membres', query: { q: searchQuery.value } });
      }
    };

    const reactiveSearch = () => {
      if (route.query.search) {
        searchQuery.value = route.query.search as string;
        searchType.value = 'articles';
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('search-products', { 
            detail: { query: route.query.search }
          }));
        }, 100);
      } else if (route.query.q) {
        searchQuery.value = route.query.q as string;
        searchType.value = 'users';
      } else {
        searchQuery.value = '';
      }
    };

    const clearSearch = () => {
      searchQuery.value = '';
      router.push({ path: '/', query: {} });
      window.dispatchEvent(new CustomEvent('search-products', { 
        detail: { query: '' } 
      }));
    };

    const showLoginModal = () => {
      showLogin.value = true;
      showRegister.value = false;
    };

    const showRegisterModal = () => {
      showRegister.value = true;
      showLogin.value = false;
    };

    const closeModals = () => {
      showLogin.value = false;
      showRegister.value = false;
    };

    const switchToLogin = () => {
      showRegister.value = false;
      showLogin.value = true;
    };

    const switchToRegister = () => {
      showLogin.value = false;
      showRegister.value = true;
    };

    const handleAuthSuccess = () => {
      closeModals();
      loadUserPhoto();
    };

    const toggleUserMenu = () => {
      showUserMenu.value = !showUserMenu.value;
    };

    const closeUserMenu = () => {
      showUserMenu.value = false;
    };

    const handleLogout = async () => {
      await clientAuth.logout();
      closeUserMenu();
      userPhoto.value = '/icone_user_defaut_orange.png';
      router.push('/');
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.value && !userMenuRef.value.contains(event.target as Node)) {
        showUserMenu.value = false;
      }
    };

    onMounted(() => {
      clientAuth.checkAuth();
      loadMainCategories();
      loadUserPhoto();
      document.addEventListener('click', handleClickOutside);
      
      window.addEventListener('user-photo-updated', handlePhotoUpdated);
      
      reactiveSearch();
      
      if (route.query.category) {
        selectedCategoryId.value = parseInt(route.query.category as string);
      }
    });

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('user-photo-updated', handlePhotoUpdated);
    });

    return {
      searchQuery,
      searchType,
      showLogin,
      showRegister,
      isAuthenticated,
      userPseudo,
      userPhoto,
      showUserMenu,
      userMenuRef,
      mainCategories,
      selectedCategoryId,
      currentRoute,
      showLoginModal,
      showRegisterModal,
      closeModals,
      switchToLogin,
      switchToRegister,
      handleAuthSuccess,
      toggleUserMenu,
      closeUserMenu,
      handleLogout,
      selectMainCategory,
      executeSearch,
      clearSearch,
      handleSellClick,
      handleAvatarError
    };
  }
});
</script>

<style lang="scss" scoped>
@use '@/styles/variables' as *;

header {
  background: white;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 10px;
}

/* Niveau 1 */
.header-top {
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}

.header-top-inner {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.logo {
  flex-shrink: 0;
}

.logo-img {
  height: 36px;
  width: auto;
}

/* Barre de recherche */
.search-wrapper {
  flex: 1;
  max-width: 600px;
  min-width: 200px;
}

.search-container {
  width: 100%;
}

.search-box {
  display: flex;
  align-items: center;
  background: #f5f5f5;
  border-radius: 40px;
  border: 1px solid #ddd;
  overflow: hidden;
  transition: all 0.2s;
  height: 40px;
  
  &:focus-within {
    border-color: $orange-doux;
    background: white;
    box-shadow: 0 0 0 3px rgba(255, 167, 84, 0.1);
  }
}

.search-type-selector {
  display: flex;
  border-right: 1px solid #ddd;
  background: #f5f5f5;
  
  .type-btn {
    padding: 8px 14px;
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    color: $gris-moyen;
    transition: all 0.2s;
    white-space: nowrap;
    
    &:hover {
      color: $orange-doux;
      background: rgba(255, 167, 84, 0.1);
    }
    
    &.active {
      color: $orange-fonce;
      background: white;
      position: relative;
      
      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: $orange-fonce;
      }
    }
  }
}

.search-input-wrapper {
  flex: 1;
  position: relative;
  
  .search-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: $gris-moyen;
    font-size: 14px;
  }
  
  .search-input {
    width: 100%;
    padding: 8px 45px 8px 38px;
    border: none;
    font-size: 14px;
    background: transparent;
    height: 40px;
    
    &:focus {
      outline: none;
    }
  }
  
  .clear-search {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: $gris-moyen;
    cursor: pointer;
    
    &:hover {
      color: $orange-fonce;
    }
  }
}

.search-btn {
  padding: 0 18px;
  background: $orange-fonce;
  color: white;
  border: none;
  cursor: pointer;
  height: 40px;
  display: flex;
  align-items: center;
  transition: background 0.2s;
  
  &:hover {
    background: darken($orange-fonce, 10%);
  }
}

/* Navigation actions */
.nav-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.btn-sell {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  background: $orange-fonce;
  color: white;
  border-radius: 30px;
  text-decoration: none;
  font-weight: 500;
  font-size: 12px;
  white-space: nowrap;
  height: 34px;
  
  svg {
    font-size: 11px;
  }
  
  span {
    font-size: 12px;
  }
  
  &:hover {
    background: darken($orange-fonce, 10%);
  }
}

/* Menu utilisateur */
.user-menu {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 30px;
  transition: background 0.2s;
  position: relative;
  background: #fafafa;
  border: 1px solid #eee;
  height: 34px;
  
  &:hover {
    background: #f5f5f5;
    border-color: $orange-doux;
  }
  
  .user-avatar {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: $orange-doux;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    overflow: hidden;
    flex-shrink: 0;
    border: 2px solid $orange-doux;
    
    .avatar-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  
  .user-pseudo {
    font-size: 12px;
    font-weight: 500;
    color: $gris-fonce;
    white-space: nowrap;
    max-width: 80px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .chevron-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    color: $orange-doux;
    transition: transform 0.3s ease;
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    
    svg {
      font-size: 10px;
      color: $orange-doux;
    }
    
    &.rotated {
      transform: rotate(180deg);
    }
  }
}

/* Dropdown */
.dropdown-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 180px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 1000;
  
  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    color: $gris-fonce;
    text-decoration: none;
    transition: background 0.2s;
    font-size: 13px;
    
    svg {
      width: 14px;
      font-size: 14px;
    }
    
    &:hover {
      background: #f5f5f5;
    }
    
    &.logout {
      color: #dc3545;
      
      svg {
        color: #dc3545;
      }
    }
    
    &.pseudo-display {
      font-weight: 600;
      cursor: default;
      background: #f8f9fa;
      
      .pseudo-full {
        color: $orange-fonce;
        font-weight: 600;
      }
      
      &:hover {
        background: #f8f9fa;
      }
    }
  }
  
  .dropdown-divider {
    margin: 4px 0;
    border: none;
    border-top: 1px solid #eee;
  }
}

/* Niveau 2 - Catégories */
.header-bottom {
  padding: 6px 0;
  background: white;
  border-bottom: 1px solid #eee;
  overflow-x: auto;
}

.categories-scroll {
  display: flex;
  gap: 20px;
  white-space: nowrap;
}

.category-link {
  padding: 4px 0;
  font-size: 13px;
  font-weight: 500;
  color: $gris-fonce;
  text-decoration: none;
  transition: color 0.2s;
  border-bottom: 2px solid transparent;
  
  &:hover {
    color: $orange-doux;
  }
  
  &.active {
    color: $orange-fonce;
    border-bottom-color: $orange-fonce;
  }
}

/* Animations */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* ✅ Responsive */
@media (max-width: 900px) {
  .search-wrapper {
    max-width: 400px;
    min-width: 150px;
  }
  
  .btn-sell {
    font-size: 11px;
    padding: 5px 10px;
    height: 30px;
    
    span {
      font-size: 11px;
    }
  }
  
  .user-pseudo {
    max-width: 60px !important;
    font-size: 11px !important;
  }
}

@media (max-width: 768px) {
  .header-top {
    padding: 6px 0;
  }
  
  .header-top-inner {
    gap: 6px;
  }
  
  .logo-img {
    height: 30px;
  }
  
  /* ✅ Barre de recherche passe en dessous */
  .search-wrapper {
    order: 3;
    max-width: 100%;
    min-width: 100%;
    flex-basis: 100%;
    margin-top: 4px;
  }
  
  .search-box {
    height: 34px;
    border-radius: 30px;
  }
  
  .type-btn {
    padding: 5px 10px !important;
    font-size: 11px !important;
  }
  
  .search-input {
    font-size: 12px !important;
    padding: 6px 35px 6px 32px !important;
    height: 34px !important;
  }
  
  .search-icon {
    font-size: 12px !important;
    left: 10px !important;
  }
  
  .search-btn {
    padding: 0 12px;
    height: 34px;
    
    svg {
      font-size: 12px;
    }
  }
  
  .btn-sell {
    font-size: 10px;
    padding: 4px 10px;
    height: 28px;
    
    svg {
      font-size: 9px;
    }
    
    span {
      font-size: 10px;
    }
  }
  
  .nav-actions {
    gap: 5px;
  }
  
  .user-menu {
    height: 28px;
    padding: 3px 8px;
    gap: 3px;
    
    .user-avatar {
      width: 22px;
      height: 22px;
    }
    
    .user-pseudo {
      max-width: 50px !important;
      font-size: 10px !important;
    }
    
    .chevron-icon {
      width: 16px;
      height: 16px;
      font-size: 9px;
      
      svg {
        font-size: 9px;
      }
    }
  }
  
  .categories-scroll {
    gap: 14px;
  }
  
  .category-link {
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .logo-img {
    height: 34px;
  }
  
  .search-box {
    height: 32px;
    border-radius: 24px;
  }
  
  .type-btn {
    padding: 4px 8px !important;
    font-size: 10px !important;
  }
  
  .search-input {
    font-size: 11px !important;
    padding: 4px 28px 4px 28px !important;
    height: 30px !important;
  }
  
  .search-icon {
    font-size: 10px !important;
    left: 8px !important;
  }
  
  .search-btn {
    padding: 0 10px;
    height: 30px;
    
    svg {
      font-size: 10px;
    }
  }
  
  .btn-sell {
    font-size: 17px;
    padding: 3px 5px;
    height: 30px;
    gap: 3px;
    
    svg {
      font-size: 9px;
    }
    
    span {
      font-size: 12px;
    }
  }
  
  .nav-actions {
    gap: 4px;
  }
  
  .user-menu {
    height: 32px;
    padding: 2px 6px;
    gap: 2px;
    
    .user-avatar {
      width: 20px;
      height: 20px;
      border-width: 1.5px;
    }
    
    .user-pseudo {
      max-width: 40px !important;
      font-size: 9px !important;
    }
    
    .chevron-icon {
      width: 14px;
      height: 14px;
      font-size: 8px;
      
      svg {
        font-size: 8px;
      }
    }
  }
  
  .categories-scroll {
    gap: 10px;
  }
  
  .category-link {
    font-size: 11px;
  }
}
</style>